import { lazy, Suspense, useCallback, useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import ErrorBoundary from "./components/UI/ErrorBoundary";
import MainLoader from "./components/UI/MainLoader";
import MainModal from "./components/UI/MainModal";
import MainSnackbar from "./components/UI/MainSnackbar";
import useApp from "./hooks/useApp";
import useLoading from "./hooks/useLoading";
import useAccount from "./hooks/useAccount";
import useFeature from "./hooks/useFeature";
import useConcept from "./hooks/useConcept";
import { DataStore } from "aws-amplify";
import {
  AdminPermissions,
  ListingByAccountVariables,
  ListingVariables,
  UserConceptsListingVariables,
} from "./models/app";
import usePermissions from "./hooks/usePermissions";
import {
  Account,
  AdminGroup,
  Concept,
  Feature,
  UserConcepts,
} from "./models/GQL_API";
import useAdminGroup from "./hooks/useAdminGroup";
import useUserConcepts from "./hooks/useUserConcepts";

const Admin = lazy(() => import("./containers/Dashboard"));
const Auth = lazy(() => import("./containers/Auth"));

const App: React.FC = () => {
  const { loading, changeLoading } = useLoading();
  const { getAdminPermissions } = usePermissions();
  const {
    modalOpen,
    snackBarOpen,
    fetchSession,
    setSession,
    clearSession,
    setPermissions,
  } = useApp();
  const { accountsFetchByDomainOnline, accountsSelected } = useAccount(
    "accounts",
    "account",
  );
  const { featuresFetchAll, featuresAllListing, featuresChangeListing } =
    useFeature("features", "feature");
  const { adminGroupsFetchCurrentUser } = useAdminGroup(
    "adminGroups",
    "adminGroup",
  );
  const { conceptsFetch, conceptsChangeListing } = useConcept(
    "concepts",
    "concept",
  );
  const { userConceptsFetch, userConceptsChangeListing } = useUserConcepts(
    "userConcepts",
    "userConcept",
  );

  const initApp = useCallback(async (session: any) => {
    try {
      setSession(session);

      const params: ListingVariables = {
        searchText: "",
        startIndex: 0,
        limit: 1000,
      };
      const userConceptParams: UserConceptsListingVariables = {
        ...params,
        userID: session?.sub,
      };

      const data = await Promise.all([
        accountsFetchByDomainOnline(session),
        featuresFetchAll({}),
        userConceptsFetch(userConceptParams),
        adminGroupsFetchCurrentUser(session.sub),
      ]);

      const account: Account = data[0];
      const features: Feature[] = data[1];
      const userConcepts: UserConcepts = data[2][0];
      const userGroup: AdminGroup[] = data[3];

      const listingByAccountParams: ListingByAccountVariables = {
        ...params,
        accountID: account.id,
      };

      const resultData: any[] = await Promise.all([
        conceptsFetch(listingByAccountParams, session),
        getAdminPermissions({
          userGroup,
        }),
      ]);

      const permissions: AdminPermissions = resultData[1];
      const concepts: Concept[] = resultData[0]; // All account concepts

      setSession(session);
      setPermissions(permissions);
      featuresChangeListing(features);
      conceptsChangeListing(concepts);
      userConceptsChangeListing(userConcepts);

      changeLoading(false);
    } catch (err: Error | any) {
      clearSession();
      changeLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  const getSession = async () => {
    try {
      const session = await fetchSession();

      initApp(session);
    } catch (err: Error | any) {
      sync();
    }
  };

  const sync = async () => {
    await DataStore.start();

    initApp(null);
  };

  useEffect(() => {
    getSession();

    // eslint-disable-next-line
  }, []);

  if (loading && (!accountsSelected || !featuresAllListing))
    return <MainLoader />;

  return (
    <ErrorBoundary>
      <DndProvider backend={HTML5Backend}>
        <Suspense fallback={<MainLoader />}>
          <Routes>
            <Route path="/*" element={<Auth />} />
            <Route path="dashboard/*" element={<Admin />} />
          </Routes>
        </Suspense>
      </DndProvider>
      {modalOpen && <MainModal />}
      {snackBarOpen && <MainSnackbar />}
    </ErrorBoundary>
  );
};

export default App;
